import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Table, Button, Spinner, Modal, Form, Badge, InputGroup, Row, Col, Card, Pagination
} from 'react-bootstrap';
import Swal from 'sweetalert2';
import useApi from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import { FaSearch, FaPlus, FaEdit, FaEye, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import AddStudentModal from '../AddStudentModal';
import EditStudentModal from '../EditStudentModal';
import './OurStudents.css'; // CSS file for custom styles

const OurStudents = () => {
  const { bearerAuth } = useApi();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [expandedRows, setExpandedRows] = useState({}); // For mobile expandable rows

  // Modals state
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showEditStudent, setShowEditStudent] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);

  // Selected enrollment
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);

  const [acceptPayload, setAcceptPayload] = useState({
    course_id: '',
    admin_exact_start_at: '',
    admin_time_slot: '',
    admin_note: '',
    admin_time_slot_custom: ''
  });
  const [acceptPreview, setAcceptPreview] = useState(null);
  const [acceptPreviewLoading, setAcceptPreviewLoading] = useState(false);

  const [rejectNote, setRejectNote] = useState('');
  const [processing, setProcessing] = useState(false);

  // TIME SLOTS
  const [timeSlots, setTimeSlots] = useState([]);
  const [timeSlotLoading, setTimeSlotLoading] = useState(false);

  // PROGRESS DATA
  const [progressData, setProgressData] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);

  // COURSE DURATIONS
  const [courseDurations, setCourseDurations] = useState({});

  const drivingSchoolId = user?.id || null;

  useEffect(() => {
    if (drivingSchoolId) {
      fetchEnrollments();
      fetchCourses();
    }
  }, [drivingSchoolId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, fromDate, toDate]);

  // Toggle expanded row for mobile view
  const toggleRowExpanded = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  /** GET TIME SLOTS */
  const fetchTimeSlots = async (date, schoolId) => {
    try {
      if (!date || !schoolId) {
        setTimeSlots(["Other"]);
        return;
      }

      setTimeSlotLoading(true);

      const formattedDate = date.slice(0, 10);

      const res = await bearerAuth(`check-timeslots`, "POST", {
        date: formattedDate,
        assignSchool: schoolId
      });

      if (res?.data?.status && Array.isArray(res.data.slots)) {
        const converted = res.data.slots.map(s => ({
          id: s.id,
          text: `${String(s.start_time || "").slice(0, 5)} - ${String(s.end_time || "").slice(0, 5)}`,
          booked: !!s.booked,
          booked_count: Number(s.booked_count || 0),
          capacity: s.capacity ?? null,
          remaining: s.remaining ?? null,
        }));

        setTimeSlots([...converted, { id: "other", text: "Other" }]);
      } else {
        setTimeSlots([{ id: "other", text: "Other" }]);
      }
    } catch (err) {
      console.error("Time slot error:", err);
      setTimeSlots([{ id: "other", text: "Other" }]);
    } finally {
      setTimeSlotLoading(false);
    }
  };

  const fetchEnrollments = async () => {
    try {
      setLoading(true);
      const res = await bearerAuth('driving-school/enrollments');

      if (res?.data?.status && Array.isArray(res.data.data)) {
        const filteredEnrollments = res.data.data.filter(
          enrollment => enrollment.assigned_school_id === drivingSchoolId
        );
        setEnrollments(filteredEnrollments);
      } else {
        setEnrollments([]);
      }
    } catch (e) {
      Swal.fire('Error', 'Failed to fetch enrollments', 'error');
      setEnrollments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      setLoadingCourses(true);
      const res = await bearerAuth('courses');
      if (res?.data?.status) {
        setCourses(res.data.data);

        const durations = {};
        res.data.data.forEach(course => {
          durations[course.id] = course.duration || 0;
        });
        setCourseDurations(durations);
      } else {
        setCourses([]);
      }
    } catch (e) {
      setCourses([]);
      setCourseDurations({});
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchStudentProgress = async (userId, courseId) => {
    try {
      setLoadingProgress(true);
      setProgressData(null);

      const res = await bearerAuth(
        `driving-school/student-progress/${userId}`,
        'GET',
        { course_id: courseId }
      );

      console.log("Progress API Response:", res);

      if (res?.data?.status) {
        const progressResponse = res.data;

        const completedClasses = progressResponse.data?.length || 0;
        const totalClasses = courseDurations[courseId] || 0;
        const testScores = progressResponse.testresult || [];
        const testScore = testScores.length > 0 ? testScores[0].score : 'N/A';

        let averageScore = 'N/A';
        if (testScores.length > 0) {
          const totalScore = testScores.reduce((sum, test) => sum + (test.score || 0), 0);
          averageScore = (totalScore / testScores.length).toFixed(1);
        }

        const formattedProgress = {
          total_classes: totalClasses,
          completed_classes: completedClasses,
          remaining_classes: Math.max(0, totalClasses - completedClasses),
          test_score: testScore,
          average_score: averageScore,
          test_results: testScores,
          lesson_details: progressResponse.data || [],
          progress_percentage: totalClasses > 0
            ? Math.round((completedClasses / totalClasses) * 100)
            : 0
        };

        setProgressData(formattedProgress);
      } else {
        const totalClasses = courseDurations[courseId] || 0;
        setProgressData({
          total_classes: totalClasses,
          completed_classes: 0,
          remaining_classes: totalClasses,
          test_score: 'N/A',
          average_score: 'N/A',
          test_results: [],
          lesson_details: [],
          progress_percentage: 0
        });
      }
    } catch (error) {
      console.error("Error fetching progress:", error);

      const courseId = selectedEnrollment?.course_id;
      const totalClasses = courseDurations[courseId] || 0;

      setProgressData({
        total_classes: totalClasses,
        completed_classes: 0,
        remaining_classes: totalClasses,
        test_score: 'N/A',
        average_score: 'N/A',
        test_results: [],
        lesson_details: [],
        progress_percentage: 0
      });

      Swal.fire('Error', 'Failed to fetch student progress', 'error');
    } finally {
      setLoadingProgress(false);
    }
  };

  const openProgressModal = async (enrollment) => {
    setSelectedEnrollment(enrollment);
    setLoadingProgress(true);
    setProgressData(null);
    setShowProgressModal(true);
    await fetchStudentProgress(enrollment.user_id, enrollment.course_id);
  };

  const toDateOnly = (value) => {
    if (!value) return null;
    const m = value.match(/^(\d{4}-\d{2}-\d{2})$/);
    return m ? m[1] : null;
  };

  const formatSlotOptionLabel = (slot) => {
    const capacity = Number(slot.capacity);
    if (capacity > 0) {
      return `${slot.text} (${slot.booked_count || 0}/${capacity})${slot.booked ? ' - Full' : ''}`;
    }
    return slot.text;
  };

  const normalizeStatus = (item) => {
    const raw = item?.status;
    
    if (raw === null || raw === undefined) return 'pending';
    if (raw === 0) return 'pending';
    if (raw === 1) return 'accepted';
    if (raw === 2) return 'rejected';
    
    if (typeof raw === 'string') {
      const lowerRaw = raw.toLowerCase();
      if (lowerRaw === 'pending') return 'pending';
      if (lowerRaw === 'accepted') return 'accepted';
      if (lowerRaw === 'rejected') return 'rejected';
      if (lowerRaw === 'scheduled') return 'scheduled';
      return lowerRaw;
    }
    
    return String(raw).toLowerCase();
  };

  const needsAction = (enrollment) => {
    const status = normalizeStatus(enrollment);
    return status === 'pending' || status === 'scheduled';
  };

  const openEditModal = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setShowEditStudent(true);
  };

  const openAccept = async (enr) => {
    setSelectedEnrollment(enr);
    setAcceptPreview(null);
    setAcceptPreviewLoading(true);

    const expectedDate = enr.expected_start_date?.slice(0, 10) || "";

    setAcceptPayload({
      course_id: enr.course_id || '',
      admin_exact_start_at: expectedDate,
      admin_time_slot: '',
      admin_note: '',
      admin_time_slot_custom: ''
    });

    setShowAcceptModal(true);

    try {
      const previewRes = await bearerAuth(`driving-school/enrollment/${enr.id}/accept-preview`);
      if (previewRes?.data?.status) {
        setAcceptPreview(previewRes.data.data || null);
      }
    } catch (e) {
      setAcceptPreview(null);
    } finally {
      setAcceptPreviewLoading(false);
    }

    await fetchTimeSlots(expectedDate, drivingSchoolId);
  };

  const submitAccept = async () => {
    const dateOnly = toDateOnly(acceptPayload.admin_exact_start_at);

    let slot = "";
    if (acceptPayload.admin_time_slot === "Other") {
      slot = acceptPayload.admin_time_slot_custom.trim();
    } else {
      slot = acceptPayload.admin_time_slot;
    }

    if (!dateOnly) {
      Swal.fire('Validation', 'Please choose exact start date.', 'warning');
      return;
    }

    if (!slot) {
      Swal.fire('Validation', 'Please choose or enter time slot.', 'warning');
      return;
    }

    const selectedSlotMeta = timeSlots.find((item) => item.text === slot);
    if (selectedSlotMeta?.booked) {
      Swal.fire('Validation', 'This time slot is full. Please choose another slot.', 'warning');
      return;
    }

    setProcessing(true);
    try {
      const body = {
        admin_exact_start_at: dateOnly,
        admin_time_slot: slot,
        admin_note: acceptPayload.admin_note || null,
        course_id: acceptPayload.course_id || null
      };

      const res = await bearerAuth(
        `driving-school/enrollment/${selectedEnrollment.id}/accept`,
        'POST',
        body
      );

      if (res?.data?.status) {
        Swal.fire('Accepted', 'Enrollment accepted.', 'success');
        setShowAcceptModal(false);
        setAcceptPreview(null);
        fetchEnrollments();
      } else {
        Swal.fire('Error', res?.data?.message || 'Failed to accept', 'error');
      }
    } catch (e) {
      Swal.fire('Error', 'Failed to accept enrollment', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const openReject = (enr) => {
    setSelectedEnrollment(enr);
    setRejectNote('');
    setShowRejectModal(true);
  };

  const submitReject = async () => {
    if (!rejectNote.trim()) {
      Swal.fire('Validation', 'Please enter reason', 'warning');
      return;
    }

    setProcessing(true);
    try {
      const body = { admin_note: rejectNote };
      const res = await bearerAuth(
        `driving-school/enrollment/${selectedEnrollment.id}/reject`,
        'POST',
        body
      );

      if (res?.data?.status) {
        Swal.fire('Rejected', 'Enrollment rejected.', 'success');
        setShowRejectModal(false);
        fetchEnrollments();
      } else {
        Swal.fire('Error', res?.data?.message || 'Failed to reject', 'error');
      }
    } catch (e) {
      Swal.fire('Error', 'Failed to reject', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const filtered = enrollments.filter(e => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (e.user?.full_name || '').toLowerCase().includes(q) ||
      (e.user?.username || '').toLowerCase().includes(q) ||
      (e.user?.email || '').toLowerCase().includes(q) ||
      (e.course?.title || '').toLowerCase().includes(q)
    );
  });

  const filteredByDate = filtered.filter((e) => {
    const dateKey = (e.admin_exact_start_at || e.expected_start_date || e.created_at || '').slice(0, 10);

    if (!dateKey) return false;

    if (fromDate && dateKey < fromDate) return false;
    if (toDate && dateKey > toDate) return false;

    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredByDate.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    return filteredByDate.slice(start, start + itemsPerPage);
  }, [filteredByDate, safePage]);

  const calculateProgressPercentage = (progress) => {
    if (!progress) return 0;
    const completed = progress.completed_classes || 0;
    const total = progress.total_classes || 0;
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getCourseDuration = (courseId) => {
    return courseDurations[courseId] || 0;
  };

  if (!drivingSchoolId) {
    return (
      <Container fluid className="px-2 px-md-4 py-4">
        <div className="text-center py-5">
          <h5>Access Restricted</h5>
          <p className="text-muted">Only driving schools can view this page.</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-2 px-md-4 py-4 our-students-container">

      {/* TOP - Responsive Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-3">
        <div>
          <h4 className="mb-1">Our Students</h4>
          <small className="text-muted d-block d-md-inline">
            Showing {filteredByDate.length} of {enrollments.length} enrollment(s) for your driving school
          </small>
        </div>

        <div className="student-filter-bar d-flex flex-column flex-xl-row align-items-stretch align-items-xl-end gap-2 w-100 w-xl-auto">
          <div className="student-filter-search">
            <InputGroup size="sm" className="w-100">
              <Form.Control
                placeholder="Search students..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border-end-0"
              />
              <Button variant="outline-secondary" className="border-start-0">
                <FaSearch />
              </Button>
            </InputGroup>
          </div>

          <div className="student-filter-range d-flex flex-column flex-sm-row gap-2">
            <InputGroup size="sm" className="student-filter-input">
              <InputGroup.Text className="bg-white">From Date</InputGroup.Text>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                aria-label="Filter from date"
              />
            </InputGroup>

            <InputGroup size="sm" className="student-filter-input">
              <InputGroup.Text className="bg-white">To Date</InputGroup.Text>
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                aria-label="Filter to date"
              />
            </InputGroup>

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setFromDate('');
                setToDate('');
              }}
              disabled={!fromDate && !toDate}
              className="student-filter-clear"
            >
              Clear
            </Button>
          </div>

          <Button 
            variant="primary" 
            onClick={() => setShowAddStudent(true)}
            className="d-flex align-items-center justify-content-center gap-2 student-add-btn"
          >
            <FaPlus /> <span className="d-sm-inline">Add Student</span>
          </Button>
        </div>
      </div>

      {/* TABLE - Desktop View (hidden on mobile) */}
      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="d-none d-lg-block table-responsive">
            <Table hover size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Student Id</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Course</th>
                  <th>Total Classes</th>
                  <th>Admin Start</th>
                  <th>Admin Slot</th>
                  <th>Status</th>
                  <th>Registered By</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {paginated.length === 0 && (
                  <tr>
                    <td colSpan="12" className="text-center py-3">
                      No students found for your driving school
                    </td>
                  </tr>
                )}

                {paginated.map((e, i) => {
                  const status = normalizeStatus(e);
                  const needsActionStatus = needsAction(e);
                  const isRegisteredByMe = e.user?.register_by === user?.id;
                  const totalClasses = getCourseDuration(e.course_id);

                  return (
                    <tr key={e.id}>
                      <td>{(safePage - 1) * itemsPerPage + i + 1}</td>
                      <td>{e.user?.id || 'N/A'}</td>
                      <td>{e.user?.full_name || e.user?.username || 'N/A'}</td>
                      <td>{e.user?.email || 'N/A'}</td>
                      <td>{e.user?.contact || 'N/A'}</td>
                      <td>{e.course?.title || 'None'}</td>
                      <td>
                        <Badge bg="info">{totalClasses}</Badge>
                      </td>
                      <td>{e.admin_exact_start_at?.slice(0, 10) || '-'}</td>
                      <td>{e.admin_time_slot || '-'}</td>

                      <td>
                        <Badge
                          bg={
                            status === 'accepted'
                              ? 'success'
                              : status === 'rejected'
                                ? 'danger'
                                : status === 'scheduled'
                                  ? 'warning'
                                  : 'secondary'
                          }
                          className="text-nowrap"
                        >
                          {status}
                        </Badge>
                      </td>

                      <td>
                        <small>
                          {isRegisteredByMe ? (
                            <Badge bg="info" className="text-nowrap">Your Registration</Badge>
                          ) : (
                            'Other'
                          )}
                        </small>
                      </td>

                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          {status === 'accepted' && (
                            <Button
                              size="sm"
                              variant="info"
                              onClick={() => openProgressModal(e)}
                              title="View Student Progress"
                              className="d-flex align-items-center gap-1"
                            >
                              <FaEye />
                            </Button>
                          )}

                          {isRegisteredByMe && status === 'accepted' && (
                            <Button
                              size="sm"
                              variant="warning"
                              onClick={() => openEditModal(e)}
                              title="Edit Enrollment"
                              className="d-flex align-items-center gap-1"
                            >
                              <FaEdit />
                            </Button>
                          )}

                          {needsActionStatus && (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => openAccept(e)}
                                className="text-nowrap"
                              >
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => openReject(e)}
                                className="text-nowrap"
                              >
                                Reject
                              </Button>
                            </>
                          )}

                          {!needsActionStatus && !isRegisteredByMe && status !== 'accepted' && (
                            <small className="text-muted text-nowrap">No actions</small>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Mobile Cards View (hidden on desktop) */}
          <div className="d-block d-lg-none">
            {paginated.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">No students found for your driving school</p>
              </div>
            ) : (
              paginated.map((e, i) => {
                const status = normalizeStatus(e);
                const needsActionStatus = needsAction(e);
                const isRegisteredByMe = e.user?.register_by === user?.id;
                const totalClasses = getCourseDuration(e.course_id);
                const isExpanded = expandedRows[e.id];

                return (
                  <Card key={e.id} className="mb-3 student-card">
                    <Card.Body className="p-3">
                      {/* Card Header - Always visible */}
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg="light" text="dark" className="px-2 py-1">
                            #{(safePage - 1) * itemsPerPage + i + 1}
                          </Badge>
                          <div>
                            <h6 className="mb-0">{e.user?.full_name || e.user?.username || 'N/A'}</h6>
                            <small className="text-muted">ID: {e.user?.id || 'N/A'}</small>
                          </div>
                        </div>
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none"
                          onClick={() => toggleRowExpanded(e.id)}
                        >
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </Button>
                      </div>

                      {/* Basic Info - Always visible */}
                      <div className="mt-2 d-flex flex-wrap gap-2">
                        <Badge
                          bg={
                            status === 'accepted'
                              ? 'success'
                              : status === 'rejected'
                                ? 'danger'
                                : status === 'scheduled'
                                  ? 'warning'
                                  : 'secondary'
                          }
                          className="px-2 py-1"
                        >
                          {status}
                        </Badge>
                        <Badge bg="info" className="px-2 py-1">
                          {e.course?.title || 'No Course'}
                        </Badge>
                      </div>

                      {/* Quick Info */}
                      <div className="mt-2 text-muted small">
                        <div className="d-flex justify-content-between">
                          <span>📧 {e.user?.email || 'N/A'}</span>
                          <span>📞 {e.user?.contact || 'N/A'}</span>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="mt-3 pt-2 border-top">
                          <Row className="g-2">
                            <Col xs={6}>
                              <small className="text-muted d-block">Total Classes</small>
                              <strong>{totalClasses}</strong>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted d-block">Admin Start</small>
                              <strong>{e.admin_exact_start_at?.slice(0, 10) || '-'}</strong>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted d-block">Admin Slot</small>
                              <strong>{e.admin_time_slot || '-'}</strong>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted d-block">Registered By</small>
                              <strong>
                                {isRegisteredByMe ? (
                                  <Badge bg="info">Your Registration</Badge>
                                ) : (
                                  'Other'
                                )}
                              </strong>
                            </Col>
                          </Row>

                          {/* Actions */}
                          <div className="mt-3 d-flex gap-2 flex-wrap">
                            {status === 'accepted' && (
                              <Button
                                size="sm"
                                variant="info"
                                onClick={() => openProgressModal(e)}
                                className="d-flex align-items-center gap-1"
                              >
                                <FaEye /> View Progress
                              </Button>
                            )}

                            {isRegisteredByMe && status === 'accepted' && (
                              <Button
                                size="sm"
                                variant="warning"
                                onClick={() => openEditModal(e)}
                                className="d-flex align-items-center gap-1"
                              >
                                <FaEdit /> Edit
                              </Button>
                            )}

                            {needsActionStatus && (
                              <>
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() => openAccept(e)}
                                >
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => openReject(e)}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                );
              })
            )}
          </div>

          {filteredByDate.length > 0 && (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
              <div className="text-muted small">
                Showing {Math.min(filteredByDate.length, (safePage - 1) * itemsPerPage + 1)} to{" "}
                {Math.min(filteredByDate.length, safePage * itemsPerPage)} of {filteredByDate.length} students
              </div>
              <Pagination className="mb-0">
                <Pagination.Prev
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={safePage === 1}
                />
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                  <Pagination.Item
                    key={page}
                    active={page === safePage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={safePage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* All Modals - Already responsive due to Bootstrap */}
      <AddStudentModal
        show={showAddStudent}
        onClose={() => setShowAddStudent(false)}
        onEnrollSuccess={() => {
          Swal.fire({
            icon: "success",
            title: "Student Added Successfully!",
            text: "Student has been registered and enrolled successfully.",
            showConfirmButton: false,
            timer: 2000,
          });
          fetchEnrollments();
          setShowAddStudent(false);
        }}
        isSchoolAdmin={true}
      />

      <EditStudentModal
        show={showEditStudent}
        onClose={() => setShowEditStudent(false)}
        onEditSuccess={() => {
          Swal.fire({
            icon: "success",
            title: "Student Updated Successfully!",
            text: "Student enrollment has been updated successfully.",
            showConfirmButton: false,
            timer: 2000,
          });
          fetchEnrollments();
          setShowEditStudent(false);
        }}
        enrollmentData={selectedEnrollment}
        isSchoolAdmin={true}
      />

      {/* VIEW PROGRESS MODAL */}
      <Modal
        show={showProgressModal}
        onHide={() => setShowProgressModal(false)}
        size="lg"
        className="progress-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fs-md-5">
            Student Progress - {selectedEnrollment?.user?.full_name || selectedEnrollment?.user?.username || 'Student'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loadingProgress ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading student progress...</p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <h6 className="text-muted mb-3">Student Information</h6>
                <Row className="g-2">
                  <Col xs={12} md={6}>
                    <div className="mb-2">
                      <strong>Student Name:</strong> {selectedEnrollment?.user?.full_name || selectedEnrollment?.user?.username || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Course:</strong> {selectedEnrollment?.course?.title || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Course Duration:</strong> {getCourseDuration(selectedEnrollment?.course_id)} classes
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div className="mb-2">
                      <strong>Enrollment Date:</strong> {formatDate(selectedEnrollment?.created_at)}
                    </div>
                    <div className="mb-2">
                      <strong>Status:</strong>
                      <Badge bg="success" className="ms-2">
                        {normalizeStatus(selectedEnrollment)}
                      </Badge>
                    </div>
                  </Col>
                </Row>
              </div>

              {progressData && (
                <>
                  <div className="mb-4">
                    <h6 className="text-muted mb-3">Progress Summary</h6>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Overall Progress</span>
                        <span className="fw-bold">
                          {progressData.progress_percentage || 0}%
                        </span>
                      </div>
                      <div className="progress" style={{ height: '10px' }}>
                        <div
                          className="progress-bar bg-success"
                          role="progressbar"
                          style={{ width: `${progressData.progress_percentage || 0}%` }}
                          aria-valuenow={progressData.progress_percentage || 0}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        ></div>
                      </div>
                    </div>

                    <Row className="g-2">
                      <Col xs={6} md={3}>
                        <div className="card border-primary h-100">
                          <div className="card-body text-center p-2 p-md-3">
                            <h3 className="text-primary mb-1">
                              {progressData.completed_classes || 0}
                            </h3>
                            <p className="text-muted mb-0 small">Completed</p>
                          </div>
                        </div>
                      </Col>

                      <Col xs={6} md={3}>
                        <div className="card border-info h-100">
                          <div className="card-body text-center p-2 p-md-3">
                            <h3 className="text-info mb-1">
                              {progressData.total_classes || 0}
                            </h3>
                            <p className="text-muted mb-0 small">Total</p>
                          </div>
                        </div>
                      </Col>

                      <Col xs={6} md={3}>
                        <div className="card border-warning h-100">
                          <div className="card-body text-center p-2 p-md-3">
                            <h3 className="text-warning mb-1">
                              {progressData.remaining_classes || 0}
                            </h3>
                            <p className="text-muted mb-0 small">Remaining</p>
                          </div>
                        </div>
                      </Col>

                      <Col xs={6} md={3}>
                        <div className="card border-success h-100">
                          <div className="card-body text-center p-2 p-md-3">
                            <h3 className="text-success mb-1">
                              {progressData.test_score || 'N/A'}
                            </h3>
                            <p className="text-muted mb-0 small">Test Score</p>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {progressData.test_results && progressData.test_results.length > 0 && (
                    <div className="mb-4">
                      <h6 className="text-muted mb-3">Test Results</h6>
                      <div className="table-responsive">
                        <Table bordered size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Test ID</th>
                              <th>Score</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {progressData.test_results.map((test, index) => (
                              <tr key={test.id}>
                                <td>Test #{test.test_id || test.id}</td>
                                <td>
                                  <Badge bg="info">{test.score || 'N/A'}</Badge>
                                </td>
                                <td>
                                  {test.taken_on ? formatDate(test.taken_on) : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {progressData.lesson_details && progressData.lesson_details.length > 0 && (
                    <div className="mb-4">
                      <h6 className="text-muted mb-3">Class Attendance</h6>
                      <div className="table-responsive">
                        <Table bordered size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Class</th>
                              <th>Date</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {progressData.lesson_details.map((lesson, index) => (
                              <tr key={lesson.id}>
                                <td>Class {index + 1}</td>
                                <td>{lesson.date ? formatDate(lesson.date) : 'N/A'}</td>
                                <td>
                                  <Badge
                                    bg={lesson.status === 'completed' ? 'success' : 'secondary'}
                                  >
                                    {lesson.status || 'pending'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProgressModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ACCEPT MODAL */}
      <Modal
        show={showAcceptModal}
        onHide={() => {
          setShowAcceptModal(false);
          setAcceptPreview(null);
          setAcceptPreviewLoading(false);
        }}
        className="accept-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fs-md-5">Accept Enrollment</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {acceptPreviewLoading ? (
              <div className="mb-3 text-center">
                <Spinner size="sm" className="me-2" />
                <span className="text-muted">Loading enrollment details...</span>
              </div>
            ) : null}

            {acceptPreview?.requested_enrollment && (
              <div className="mb-3 p-3 rounded border bg-light">
                <div className="fw-semibold mb-2">Student Preferences</div>
                <div className="small text-muted mb-1">
                  Enrollment for: <strong>{acceptPreview.requested_enrollment.enrollment_for || 'self'}</strong>
                </div>
                <div className="small text-muted mb-1">
                  DOB / Gender: <strong>{acceptPreview.requested_enrollment.dob || 'N/A'}</strong> / <strong>{acceptPreview.requested_enrollment.gender || 'N/A'}</strong>
                </div>
                <div className="small text-muted mb-1">
                  Requested duration: <strong>{acceptPreview.requested_enrollment.duration_days} day(s)</strong>
                </div>
                <div className="small text-muted mb-1">
                  Requested window: <strong>{acceptPreview.requested_enrollment.requested_start_date || 'N/A'}</strong>
                  {acceptPreview.requested_enrollment.requested_end_date
                    ? ` to ${acceptPreview.requested_enrollment.requested_end_date}`
                    : ''}
                </div>
                <div className="small text-muted">
                  Preferences:
                  <strong>
                    {acceptPreview.requested_enrollment.preferences?.female_driver_only ? ' Female driver only' : ' Any driver'}
                    {acceptPreview.requested_enrollment.preferences?.nearby_only ? ' | Nearby preferred' : ''}
                    {acceptPreview.requested_enrollment.preferences?.minimum_rating ? ` | Min rating ${acceptPreview.requested_enrollment.preferences.minimum_rating}` : ''}
                    {acceptPreview.requested_enrollment.preferences?.preferred_car_type ? ` | Car ${acceptPreview.requested_enrollment.preferences.preferred_car_type}` : ''}
                  </strong>
                </div>
              </div>
            )}

            {acceptPreview?.related_enrollments?.length ? (
              <div className="mb-3 p-3 rounded border" style={{ background: "#fff8e8", borderColor: "#f4d58d" }}>
                <div className="fw-semibold mb-2">Existing Ongoing / Conflicting Enrollments</div>
                {acceptPreview.related_enrollments.map((item) => (
                  <div key={item.id} className="small mb-2 pb-2 border-bottom">
                    <div>
                      <strong>{item.course_title}</strong> ({item.duration_days} day{item.duration_days > 1 ? 's' : ''})
                    </div>
                    <div className="text-muted">
                      {item.start_date || 'N/A'} to {item.end_date || 'N/A'} | {item.time_slot || 'No slot'}
                    </div>
                    <div>
                      <Badge bg={item.overlaps_requested ? 'danger' : item.is_ongoing ? 'warning' : 'secondary'}>
                        {item.overlaps_requested ? 'Conflicts with requested dates' : item.is_ongoing ? 'Currently ongoing' : item.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            <Row className="g-2">
              <Col xs={12} md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Exact Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={acceptPayload.admin_exact_start_at || ''}
                    onChange={(e) => {
                      setAcceptPayload({
                        ...acceptPayload,
                        admin_exact_start_at: e.target.value
                      });
                      fetchTimeSlots(e.target.value, drivingSchoolId);
                    }}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Time Slot</Form.Label>

                  {timeSlotLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <Form.Select
                      value={acceptPayload.admin_time_slot}
                      onChange={(e) =>
                        setAcceptPayload({
                          ...acceptPayload,
                          admin_time_slot: e.target.value
                        })
                      }
                    >
                      <option value="">Choose time slot</option>
                      {timeSlots.map((slot) => (
                        <option key={slot.id} value={slot.text} disabled={slot.id !== "other" && slot.booked}>
                          {formatSlotOptionLabel(slot)}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </Form.Group>

                {acceptPayload.admin_time_slot === 'Other' && (
                  <Form.Group className="mb-2">
                    <Form.Label>Custom Slot</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. 09:30 - 11:30"
                      value={acceptPayload.admin_time_slot_custom}
                      onChange={(e) =>
                        setAcceptPayload({
                          ...acceptPayload,
                          admin_time_slot_custom: e.target.value
                        })
                      }
                    />
                  </Form.Group>
                )}
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Label>Note (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={acceptPayload.admin_note}
                onChange={(e) =>
                  setAcceptPayload({
                    ...acceptPayload,
                    admin_note: e.target.value
                  })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowAcceptModal(false);
              setAcceptPreview(null);
              setAcceptPreviewLoading(false);
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" disabled={processing} onClick={submitAccept}>
            {processing ? <Spinner size="sm" /> : 'Confirm Accept'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* REJECT MODAL */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reject Enrollment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" disabled={processing} onClick={submitReject}>
            {processing ? <Spinner size="sm" /> : 'Confirm Reject'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default OurStudents;
